import Icon from './shared/Icon';
import Chip from './shared/Chip';
import Btn from './shared/Btn';
import ModeToggle from './ModeToggle';

const STATUS_LABEL = { draft: 'Draft', validated: 'Ready to Submit', submitted: 'Submitted' };

/**
 * @param {{
 *   patientName?: string,
 *   draftStatus: 'draft'|'validated'|'submitted',
 *   saveState: 'idle'|'pending'|'saving'|'saved'|'error',
 *   lastSavedAt?: Date|null,
 *   mode: 'guided'|'expert',
 *   onModeChange: (m: 'guided'|'expert') => void,
 *   onReset: () => void,
 *   onDiscardLocal?: () => void,
 *   onCloneAndEdit: () => void,
 *   onReopenForEditing?: (() => void)|null,
 *   onRetrySave: () => void,
 *   editable: boolean,
 *   hasDirty?: boolean,
 * }} props
 */
export default function ClaimTopBar({
  patientName, draftStatus, saveState, lastSavedAt,
  mode, onModeChange, onReset, onDiscardLocal, onCloneAndEdit, onReopenForEditing = null, onRetrySave, editable, hasDirty = false,
  onCreateFromPdf = null,
}) {
  const indicator = renderSaveIndicator(saveState, onRetrySave);
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 16,
      padding: '12px 24px',
      borderBottom: '1px solid var(--ab-border-hair)',
      background: 'var(--ab-bg)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--ab-fg-subtle)' }}>
        <span>Claims</span>
        <Icon name="chevron-right" size={12} />
        <span style={{ color: 'var(--ab-fg)', fontWeight: 'var(--ab-weight-semi)' }}>
          {patientName || 'New Claim'}
        </span>
      </div>
      <div style={{ flex: 1 }} />
      <ModeToggle mode={mode} onChange={onModeChange} disabled={!editable} />
      <Chip tone={/** @type {any} */ (draftStatus)} icon={draftStatus === 'submitted' ? 'check' : 'circle-dot'}>
        {STATUS_LABEL[draftStatus] || 'Draft'}
      </Chip>
      {indicator}
      {editable ? (
        <>
          {onDiscardLocal && hasDirty && (
            <span title="Discard pending edits and re-load from server">
              <Btn kind="ghost" size="sm" icon="undo-2" onClick={onDiscardLocal}>Discard edits</Btn>
            </span>
          )}
          <span title="Abandon this draft and start a new claim">
            <Btn kind="ghost" size="sm" icon="file-plus" onClick={onReset}>Start new</Btn>
          </span>
          {onCreateFromPdf && (
            <span title="Create a claim by uploading a CMS-1500 or EOB PDF">
              <Btn kind="ghost" size="sm" icon="file-up" onClick={onCreateFromPdf}>Create from PDF</Btn>
            </span>
          )}
        </>
      ) : (
        <>
          {onReopenForEditing && (
            <Btn kind="ghost" size="sm" icon="lock-open" onClick={onReopenForEditing}>Reopen for editing</Btn>
          )}
          <Btn kind="ghost" size="sm" icon="copy" onClick={onCloneAndEdit}>Edit a copy</Btn>
        </>
      )}
    </div>
  );
}

function renderSaveIndicator(/** @type {string} */ saveState, /** @type {() => void} */ onRetry) {
  const base = { fontSize: 12, color: 'var(--ab-fg-subtle)', display: 'inline-flex', alignItems: 'center', gap: 6 };
  if (saveState === 'pending') return <span style={base}>Editing…</span>;
  if (saveState === 'saving') return <span style={base}><Icon name="cloud-upload" size={12} />Saving…</span>;
  if (saveState === 'error') {
    return (
      <button type="button" onClick={onRetry} style={{
        ...base, color: 'var(--ab-denied)', border: 'none', background: 'transparent', cursor: 'pointer',
      }}>Save failed — retry?</button>
    );
  }
  // idle | saved
  return <span style={base}><Icon name="cloud-check" size={12} />Autosaved</span>;
}
